import React, { useId } from 'react'
import clsx from 'clsx'
import Select from 'react-select'
import './ApplyV2SelectForLanguage.scss'

interface ApplyV2SelectProps {
    value?: string | number | boolean | null
    isRequired?: boolean
    label?: string
    className?: string
    id?: string | number
    name?: string
    isDisabled?: boolean
    isLoading?: boolean
    errorMessage?: string | null | boolean
    isShowErrorMessage?: boolean
    onChange?: (value: string | number | null) => void
    onFocus?: React.FocusEventHandler<HTMLDivElement>
    onBlur?: React.FocusEventHandler<HTMLDivElement>
    options?: { value: string | number; label?: string | null }[]
    placeholder?: string | number
    isSearchable?: boolean
    isClearable?: boolean
    isMulti?: boolean
    menuIsOpen?: boolean
    onMenuOpen?: () => void
    onMenuClose?: () => void
    autoFocus?: boolean
    openMenuOnFocus?: boolean
}

const baseClass = 'ApplyV2SelectForLanguage'

function ApplyV2SelectForLanguage({
    value,
    isRequired = false,
    label,
    className,
    id,
    name,
    isDisabled = false,
    isLoading = false,
    errorMessage,
    isShowErrorMessage = true,
    onChange = () => { },
    onFocus = () => { },
    onBlur = () => { },
    options = [],
    placeholder = '',
    isClearable = false,
    isSearchable = false,
    isMulti = false,
    menuIsOpen,
    onMenuOpen,
    onMenuClose,
    autoFocus,
    openMenuOnFocus,
}: ApplyV2SelectProps) {
    const defaultId = useId()
    const optionsWithLabels = options.map((option) => ({
        value: option.value,
        label: option.label ?? option.value.toString(),
    }))

    const selectValue = optionsWithLabels.find((option) => option.value === value)
    const selectId = id ? id.toString() : defaultId

    // 🧠 Debug logs
    console.log('%c[ApplyV2Select]', 'color: cyan')
    console.log('→ raw value:', value)
    console.log('→ options:', optionsWithLabels)
    console.log('→ computed selectValue:', selectValue)

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
                [`${baseClass}--error`]: errorMessage,
                [`${className}--disabled`]: className && isDisabled,
                [`${className}--loading`]: className && isLoading,
                [`${className}--error`]: className && errorMessage,
            })}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={name ?? selectId}>
                    {label}
                    {isRequired && '*'}
                </label>
            )}
            <Select
                inputId={name ?? selectId}
                id={name ?? selectId}
                name={name}
                value={selectValue}
                options={optionsWithLabels}
                placeholder={placeholder}
                isClearable={isClearable}
                onChange={(e) => {
                    console.log('→ selected value changed:', e?.value)
                    onChange(e ? e.value : null)
                }}
                onFocus={onFocus}
                onBlur={onBlur}
                isDisabled={isDisabled}
                isLoading={isLoading}
                isSearchable={isSearchable}
                className={clsx(`${baseClass}__input`, {
                    [`${className}__input`]: className,
                })}
                classNamePrefix={`${baseClass}__input`}
                menuIsOpen={menuIsOpen}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                autoFocus={autoFocus}
                openMenuOnFocus={openMenuOnFocus}
            />
            {errorMessage && isShowErrorMessage && (
                <div
                    className={clsx(`${baseClass}__error`, {
                        [`${className}__error`]: className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__error--icon`, {
                            [`${className}__error--icon`]: className,
                        })}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0)">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8 1.933c-3.35 0-6.067 2.716-6.067 6.067 0 3.35 2.716 6.067 6.067 6.067 3.35 0 6.067-2.716 6.067-6.067 0-3.35-2.716-6.067-6.067-6.067zM0.733 8c0-4.013 3.254-7.267 7.267-7.267 4.013 0 7.267 3.254 7.267 7.267 0 4.013-3.254 7.267-7.267 7.267-4.013 0-7.267-3.254-7.267-7.267zm7.267 1.267a.667.667 0 01-.6-.6V4.667a.667.667 0 011.333 0v4a.667.667 0 01-.733.6zm0 2.067a.667.667 0 100-1.333.667.667 0 000 1.333z"
                                    fill="#B62626"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0">
                                    <rect width="16" height="16" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                    <div
                        className={clsx(`${baseClass}__error--text`, {
                            [`${className}__error--text`]: className,
                        })}
                    >
                        {errorMessage}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2SelectForLanguage
